import { Box, Button, Collapse, Group, Paper } from "@mantine/core";
import { IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import { DateTime } from "luxon";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  useMantineReactTable
} from "mantine-react-table-open";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getMinimalMRTOptions } from "../../../../mantine/getMinimalMRTOptions";
import type { Participation } from "../../../../utils/backend-types";
import { backendDateOrDateTimeToLuxonDateTime } from "../../../../utils/dates/backendDateOrDateTimeToLuxonDateTime";
import { luxonDateTimeToBackendDateOrDateTime } from "../../../../utils/dates/luxonDateTimeToBackendDateOrDateTime";
import type {
  ChangeableFieldData,
  ChangeableFieldWidgetOptions
} from "../../../ChangeableFieldWidget/ChangeableFieldWidget";
import { ChangeableFieldWidget } from "../../../ChangeableFieldWidget/ChangeableFieldWidget";
import { ParticipationWarningModal } from "./ParticipationWarningModal/ParticipationWarningModal";
import { StatusCell } from "./StatusCell";

const CHANGEABLE_FIELD_WIDGET_OPTIONS: ChangeableFieldWidgetOptions = {
  addButtonText: "Status hinzufügen",
  addNullStartDateWhenNoValues: true,
  endDatePlaceholder: "-",
  startDatePlaceholder: "-",
  deleteTooltipText: "Status entfernen",
  numberValueHeader: "Status",
  saveButtonText: "Übernehmen",
  dateCellWidth: 120,
  allowDeletionOfAllRows: true
};

interface LocalParticipation {
  id?: number;
  firstDate: string | null;
  lastDate: string | null;
  value: boolean;
}

interface ParticipationFieldEditorProps {
  participations?: Array<Participation>;
  onChange: (participations: Array<Participation>) => void;
}

function ParticipationFieldEditor({
  participations = [],
  onChange
}: ParticipationFieldEditorProps) {
  const [isHistoryCollapseOpen, setIsHistoryCollapseOpen] = useState(false);
  const [isCollapseOpen, setIsCollapseOpen] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [submittableData, setSubmittableData] = useState<
    ChangeableFieldData<boolean>[]
  >([]);

  const [initialParticipations, setInitialParticipations] =
    useState<Array<LocalParticipation>>();

  useEffect(() => {
    if (!initialParticipations && participations) {
      setInitialParticipations(participations);
    }
  }, [initialParticipations, participations]);

  const options = useMemo(() => {
    return {
      ...CHANGEABLE_FIELD_WIDGET_OPTIONS
    };
  }, []);

  function handleClickHistoryButton() {
    setIsCollapseOpen(false);
    setIsHistoryCollapseOpen((open) => !open);
  }

  function handleClickEditButton() {
    setIsHistoryCollapseOpen(false);
    setIsCollapseOpen((open) => !open);
  }

  const handleSaveParticipations = useCallback(
    (newParticipations: Array<LocalParticipation>) => {
      const formattedParticipations: Array<Participation> =
        newParticipations.map((p) => ({
          id: p.id || 0, // Backend expects number, not optional
          firstDate: p.firstDate,
          lastDate: p.lastDate,
          value: p.value
        }));

      onChange(formattedParticipations);
      setIsCollapseOpen(false);
      return Promise.resolve();
    },
    [onChange]
  );

  const submitParticipations = useCallback(
    (changeableFieldData: Array<ChangeableFieldData<boolean>>) => {
      const newParticipations =
        convertChangeableFieldDataToParticipations(changeableFieldData);

      return handleSaveParticipations(newParticipations);
    },
    [handleSaveParticipations]
  );

  function convertParticipationsToChangeableFieldData(
    participations?: Array<LocalParticipation>
  ) {
    if (!participations) {
      return undefined;
    }

    return participations
      .map<ChangeableFieldData<boolean>>((participation) => ({
        id: participation.id,
        startDate: participation.firstDate
          ? backendDateOrDateTimeToLuxonDateTime(participation.firstDate)
          : null,
        endDate: participation.lastDate
          ? backendDateOrDateTimeToLuxonDateTime(participation.lastDate)
          : null,
        value: participation.value
      }))
      .sort((a, b) => {
        if (a.startDate === null && b.startDate === null) {
          return 0;
        } else if (a.startDate === null) {
          return -1;
        } else if (b.startDate === null) {
          return 1;
        } else if (a.startDate < b.startDate) {
          return -1;
        } else if (b.startDate < a.startDate) {
          return 1;
        }

        return 0;
      });
  }

  function convertChangeableFieldDataToParticipations(
    values: Array<ChangeableFieldData<boolean>>
  ) {
    return values.map<LocalParticipation>((value) => ({
      id: value.id,
      value: !!value.value,
      firstDate: value.startDate
        ? luxonDateTimeToBackendDateOrDateTime(value.startDate)
        : null,
      lastDate: value.endDate
        ? luxonDateTimeToBackendDateOrDateTime(value.endDate)
        : null
    }));
  }

  function getCurrentParticipation(participations: Array<LocalParticipation>) {
    const now = DateTime.now();
    return participations.find((participation) => {
      const firstDate = participation.firstDate
        ? backendDateOrDateTimeToLuxonDateTime(participation.firstDate)
        : null;
      const lastDate = participation.lastDate
        ? backendDateOrDateTimeToLuxonDateTime(participation.lastDate)
        : null;

      const firstDateInRange = !firstDate || firstDate <= now;
      const lastDateInRange = !lastDate || now <= lastDate;

      return firstDateInRange && lastDateInRange;
    });
  }

  const currentParticipation = useMemo(() => {
    return getCurrentParticipation(participations);
  }, [participations]);

  const suggestedChangeableParticipations = useMemo(() => {
    return convertParticipationsToChangeableFieldData(participations);
  }, [participations]);

  const memoizedChangeableFieldWidget = useMemo(() => {
    if (!suggestedChangeableParticipations) {
      return null;
    }
    return (
      <ChangeableFieldWidget
        dataName="Statusänderungen"
        initialValues={suggestedChangeableParticipations}
        options={options}
        valueColumns={[
          {
            header: "Status*",
            accessorKey: "value",
            minSize: 120,
            Cell: (props, onChange) => (
              <StatusCell
                statusValue={props.cell.getValue<boolean | undefined>()}
                onChange={(value) => onChange(props.row.index, value)}
              />
            )
          }
        ]}
        onCancel={() => setIsCollapseOpen(false)}
        onDone={(submittableData) => {
          if (
            JSON.stringify(
              convertParticipationsToChangeableFieldData(initialParticipations)
            ) !== JSON.stringify(submittableData)
          ) {
            setShowWarningModal(true);

            setSubmittableData(
              submittableData as ChangeableFieldData<boolean>[]
            );
          } else {
            submitParticipations(
              submittableData as ChangeableFieldData<boolean>[]
            );
          }
          return Promise.resolve();
        }}
      />
    );
  }, [
    initialParticipations,
    options,
    submitParticipations,
    suggestedChangeableParticipations
  ]);

  return (
    <div className="ChangeableParticipationField">
      {showWarningModal && (
        <ParticipationWarningModal
          onCancel={() => {
            setSubmittableData([]);
            setShowWarningModal(false);
          }}
          onSubmit={() => {
            setShowWarningModal(false);
            return submitParticipations(submittableData);
          }}
        />
      )}
      <Group mb="xs">
        <Button
          flex={1}
          justify="flex-start"
          leftSection={
            isHistoryCollapseOpen ? (
              <IconChevronDown size="1.4em" strokeWidth={1} />
            ) : (
              <IconChevronRight size="1.4em" strokeWidth={1} />
            )
          }
          variant="default"
          onClick={handleClickHistoryButton}
        >
          {currentParticipation
            ? currentParticipation.value
              ? "Ja"
              : "Nein"
            : undefined}
        </Button>
        <Button disabled={isCollapseOpen} onClick={handleClickEditButton}>
          Statusänderung hinzufügen
        </Button>
      </Group>
      <Collapse expanded={isHistoryCollapseOpen}>
        {participations && (
          <ParticipationHistory participations={participations} />
        )}
      </Collapse>
      <Collapse expanded={isCollapseOpen}>
        <Paper bd="1px solid neutral.3" mt="xs" shadow="none">
          {memoizedChangeableFieldWidget}
        </Paper>
      </Collapse>
    </div>
  );
}

interface ParticipationHistoryProps {
  participations: Array<LocalParticipation>;
}

function ParticipationHistory({ participations }: ParticipationHistoryProps) {
  const columns: Array<MRT_ColumnDef<LocalParticipation>> = [
    {
      header: "Von",
      accessorFn: (rowData) => rowData.firstDate || "-",
      minSize: 85
    },
    {
      header: "Bis",
      accessorFn: (rowData) => rowData.lastDate || "-",
      minSize: 85
    },
    {
      header: "Status",
      accessorFn: (rowData) => (rowData.value ? "Ja" : "Nein"),
      size: 120
    }
  ];

  const mrtTable = useMantineReactTable<LocalParticipation>({
    ...getMinimalMRTOptions({ className: "ParticipationHistory" }),
    columns,
    data: participations
  });

  return (
    <Box mt="xs">
      <MantineReactTable table={mrtTable} />
    </Box>
  );
}

export { ParticipationFieldEditor };
